import Spacer from '@berlitz/spacer'
import PortalStudentProfile from '@classes/StudentProfile'
import PortalLearningPath from '@classes/LearningPath'
import Collapse from '@components/Collapse'
import LPNotificationCard from '@components/LPNotificationCard'
import MaterialNoLearningPath from '@components/MaterialNoLearningPath'
import { TransparentModal } from '@components/Modal'
import { StoreProps } from '@interfaces/StoreState'
import { StudentLanguageMaterial } from '@interfaces/StudentLanguageMaterial'
import { Provider } from '@layouts/LearningPath/Context'
import { SkillsTutorPanelPath } from '@features/skillsTutor/components'
import { NOLPID } from '@utils/constants'
import { getStorageAdapter, UI_KEYS } from '@shell/adapters/storage'
import React, { Fragment, useMemo, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { Col, Row } from 'react-styled-flexboxgrid'
import { LayoutProp } from '@layouts/LearningPath/Layout/interface'
import { Content, MaintenanceBannerContainer, PageHeaderCtr, StyledVideo } from '@layouts/LearningPath/Layout/style'
import EmptyLearningPathV2 from '@components/EmptyLearningPathV2'
import EmptyLearningPathV3 from '@components/EmptyLearningPathV3'
import MaintenanceBanner from '@components/EmptyLearningPathV3/MaintenanceBanner'
import PrepareNextLevelBanner from '@components/PreparingNxtLvlBanner'
import Loader from '@layouts/LearningPath/Layout/Loader'
import { SelectSkillProductsModal } from '@features/skillsTutor/components'
import Button from '@components/Button'
import { ButtonWrapper } from '@layouts/LearningPath/Layout/style'
import WarningModal from '@components/Modal/WarningModal'
import { useSkillsTutorSelection } from '@features/skillsTutor/hooks'
import { useSkillsTutorAutoCompletion } from '@features/skillsTutor/hooks'

const useSkillsTutorContextState = (LearningPaths: LayoutProp['LearningPaths'], activeSkillsTutorLPID?: string) => {
  const [active, setActive] = React.useState(0)
  const [activeHover, setActiveHover] = React.useState(0)
  const [playVideo, setPlayVideo] = React.useState('')
  const [validPath, setValidPath] = React.useState<PortalLearningPath[] | undefined>(
    (LearningPaths?.getSelectedLearningPath(activeSkillsTutorLPID) || [])?.filter((path): path is PortalLearningPath =>
      Boolean(path)
    )
  )

  useEffect(() => {
    const selectedPath = (LearningPaths?.getSelectedLearningPath(activeSkillsTutorLPID) || [])?.filter(
      (path): path is PortalLearningPath => Boolean(path)
    )
    setValidPath(selectedPath)
  }, [activeSkillsTutorLPID, LearningPaths])

  const contextValue = {
    active,
    activeHover,
    playVideo,
    setActive,
    setActiveHover,
    setPlayVideo,
  }

  return { contextValue, validPath }
}

const SkillsTutorErrorView: React.FC<{ error: Error | undefined }> = ({ error }) => {
  if (!error) return null

  const message = error.message.toLowerCase()

  if (message.includes('user not found')) {
    return (
      <MaintenanceBannerContainer>
        <PrepareNextLevelBanner />
      </MaintenanceBannerContainer>
    )
  }

  if (message.includes('graphql')) {
    return (
      <MaintenanceBannerContainer>
        <EmptyLearningPathV3 description={error.message} />
      </MaintenanceBannerContainer>
    )
  }

  return (
    <MaintenanceBannerContainer>
      <EmptyLearningPathV2 />
    </MaintenanceBannerContainer>
  )
}

const SkillsTutorHeaderSection: React.FC<{
  validPath: PortalLearningPath[] | undefined
  studentProfile: PortalStudentProfile
  assignedSkillsTutorMaterials: StudentLanguageMaterial[]
  onOpenModal: () => void
}> = ({ validPath, studentProfile, assignedSkillsTutorMaterials, onOpenModal }) => {
  const hasValidPath = Boolean(validPath && validPath.length > 0)
  const hasExpiredContract = Boolean(validPath?.[0]?.HasContractEnded)

  return (
    <>
      {hasValidPath && (
        <ButtonWrapper>
          <Button narrow onClick={onOpenModal}>
            {assignedSkillsTutorMaterials && assignedSkillsTutorMaterials.length === 1 ? (
              <FormattedMessage id="Add Tutor" />
            ) : (
              <FormattedMessage id="Change Tutor" />
            )}
          </Button>
        </ButtonWrapper>
      )}

      {hasValidPath && hasExpiredContract && <Spacer size="sm" />}

      <Row>
        <Col xs={12} md={12} lg={studentProfile.AtRiskProgram ? 6 : 12}>
          <PageHeaderCtr>
            {validPath?.[0] && !studentProfile.AtRiskProgram ? (
              <LPNotificationCard path={validPath[0]} isSkillsTutorProgram={true} />
            ) : null}
          </PageHeaderCtr>
          {(assignedSkillsTutorMaterials.length > 1 ||
            (!studentProfile.AtRiskProgram && assignedSkillsTutorMaterials.length <= 1)) && <Spacer size="md" />}
        </Col>
      </Row>
      <Spacer size="xxxs" />
    </>
  )
}

const SkillTutorTabLayout: React.FC<LayoutProp> = ({
  error,
  LearningPaths,
  skillsTutorProducts,
  skillsTutorMaterials,
  skillsTutorMaterialsLoaded,
  activeLearningLanguage,
  onSkillsTutorDataRefresh,
  onSkillsTutorDataRefreshUntilConsistent,
  isSkillsTutorRefreshing,
  skillsTutorRefreshPhase,
}) => {
  const { studentProfile, activeStudentLanguageProfile } = useSelector(({ userProfile }: StoreProps) => ({
    profile: userProfile.info,
    studentProfile: new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo),
    activeStudentLanguageProfile:
      userProfile.info?.StudentProfile?.StudentProfileInfo?.StudentLanguageProfileInfos?.find(
        (slp) => slp?.Language === activeLearningLanguage
      ),
  }))

  const activeSkillsTutorLPID = skillsTutorMaterials?.find(
    (material) => material?.IsSkillsTutorMaterial && material?.Status === 'Assigned'
  )?.MaterialLPID

  const [postError, setPostError] = React.useState(false)

  const {
    isModalOpen,
    openModal,
    closeModal,
    assignedSkillsTutorMaterials,
    completedSkillsTutorMaterials,
    remainingSwitches,
    studentLanguageProfileId,
  } = useSkillsTutorSelection({
    activeStudentLanguageProfile,
    skillsTutorMaterials,
    skillsTutorMaterialsLoaded,
  })

  const { hasError: autoCompletionError, isUpdating } = useSkillsTutorAutoCompletion({
    learningPaths: LearningPaths,
    refresh: onSkillsTutorDataRefresh,
    isRefreshing: isSkillsTutorRefreshing,
  })

  useEffect(() => {
    if (autoCompletionError) {
      setPostError(true)
    }
  }, [autoCompletionError])

  const PathHasNoLPID = useMemo(() => activeSkillsTutorLPID?.split('-')?.[0] === NOLPID, [activeSkillsTutorLPID])

  // Clear any reference for CT in local storage
  useEffect(() => {
    getStorageAdapter().set(UI_KEYS.CT_REFRESH_REF, '')
  }, [])

  const { contextValue, validPath } = useSkillsTutorContextState(LearningPaths, activeSkillsTutorLPID)

  // Error and empty states
  if (error) {
    return <SkillsTutorErrorView error={error} />
  }

  if (postError) {
    console.error('An error occurred while posting the registration materials:', postError)
    return (
      <WarningModal
        open={postError}
        onClose={() => setPostError(false)}
        onCancel={() => setPostError(false)}
        anApiError
      />
    )
  }

  const hasLearningPaths = Boolean(LearningPaths?.learningPaths?.length)
  const hasAssignedMaterials = Boolean(assignedSkillsTutorMaterials && assignedSkillsTutorMaterials.length > 0)

  // Render the modal outside the main content flow so it's always available
  // This allows the modal to auto-open even when there are no assigned materials
  const modalComponent = (
    <SelectSkillProductsModal
      skillsTutorOptions={skillsTutorProducts}
      open={isModalOpen && !error && !postError}
      onClose={closeModal}
      onUpdated={onSkillsTutorDataRefreshUntilConsistent}
      refreshPhase={skillsTutorRefreshPhase}
      isRefreshing={isSkillsTutorRefreshing}
      remainingSwitches={remainingSwitches}
      assignedMaterials={assignedSkillsTutorMaterials}
      completedMaterials={completedSkillsTutorMaterials}
      studentProfileId={studentProfile?.SFId}
      studentLanguageProfileId={studentLanguageProfileId}
      activeLearningLanguage={activeLearningLanguage}
    />
  )

  // Show empty state when no assigned materials, but still render the modal
  if (!hasAssignedMaterials) {
    return (
      <>
        <MaintenanceBannerContainer>
          <MaintenanceBanner
            header={<FormattedMessage id="No Skills Tutor Selected" defaultMessage="No Skills Tutor Selected" />}
            subHeader={
              <FormattedMessage
                id="Please select at least one Skills Tutor to show the learning path."
                defaultMessage="Please select at least one Skills Tutor to show the learning path."
              />
            }
            btnText2={<FormattedMessage id="Add Tutor" />}
            onButtonClick2={openModal}
          />
        </MaintenanceBannerContainer>
        {modalComponent}
      </>
    )
  }

  return hasLearningPaths ? (
    <>
      <SkillsTutorHeaderSection
        validPath={validPath}
        studentProfile={studentProfile}
        assignedSkillsTutorMaterials={assignedSkillsTutorMaterials}
        onOpenModal={openModal}
      />

      {isUpdating && (
        <MaintenanceBannerContainer>
          <FormattedMessage id="SkillsTutorUpdatingStatus" defaultMessage="We are updating your Skills Tutor status…" />
        </MaintenanceBannerContainer>
      )}

      {LearningPaths?.LearningPaths?.length ? (
        <>
          {(LearningPaths?.LearningPaths || [null])?.map((path, index) => (
            <Fragment key={`valid-path-${index}`}>
              <Provider value={contextValue}>
                <Content>
                  <Collapse activeIds={[path?.Id, `${path?.Id}-culture`, 'error-boundary']}>
                    <div key={path?.Id}>
                      <SkillsTutorPanelPath
                        Path={path || undefined}
                        SkillsTutorMaterial={skillsTutorMaterials?.find((m) => m.MaterialLPID === path?.Id)}
                      />
                    </div>
                  </Collapse>
                </Content>
              </Provider>
            </Fragment>
          ))}
        </>
      ) : validPath?.[0]?.HasNoUnits || PathHasNoLPID ? (
        <MaterialNoLearningPath />
      ) : (
        validPath?.[0] !== null && (
          <>
            <LPNotificationCard path={validPath?.[0]} isSkillsTutorProgram={true} noActiveSkillsTutor={true} />
            <Spacer size="xxxl" />
          </>
        )
      )}

      <TransparentModal
        open={Boolean(contextValue.playVideo)}
        onClose={() => {
          contextValue.setPlayVideo('')
        }}
      >
        <StyledVideo width="620" height="540" controls>
          <source src={contextValue.playVideo} />
        </StyledVideo>
      </TransparentModal>
      <Spacer size="xxxl" />
      {modalComponent}
    </>
  ) : (
    <>
      <Loader />
      {modalComponent}
    </>
  )
}

export default SkillTutorTabLayout
